import { ChatMessageDetails } from "@virtualprogaming/js-public-sdk";
import classNames from "classnames";
import { isNil, noop, partial } from "lodash-es";
import moment from "moment";
import React, { FC, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { useChatDeleteMessage } from "~/queries/chat/useChatDeleteMessage";
import { Icon, LoadingIcon } from "~/ui/components/icons";
import {
  MessageTheme,
  useChatAuthAccessToken,
  useChatAuthUserData,
  useChatMessageTheme,
  useChatMessageToEdit,
  useChatSettings,
  useToast,
} from "~/ui/context";
import { convertEmojisToIcons, urlify, useScreenMd } from "~/utils";

import { ChatUsersImage } from "../../chatUserImage/ChatUserImage";

export const Message: FC<{
  message: ChatMessageDetails;
  isSending: boolean;
}> = ({ message, isSending }) => {
  const { push } = useHistory();
  const accessToken = useChatAuthAccessToken();
  const { chatAuthUser } = useChatAuthUserData();
  const { setIsChatOpen } = useChatSettings();
  const toast = useToast();
  const intl = useIntl();
  const [messageHovered, setMessageHovered] = useState<number | null>(null);
  const { messageTheme } = useChatMessageTheme();
  const { messageToEdit, setMessageToEdit } = useChatMessageToEdit();
  const {
    mutateAsync: deleteMessage,
    isLoading: isMessageDeleting,
  } = useChatDeleteMessage();

  const handleDeleteMessage = useCallback(
    (id: number | null) => {
      if (!id) return;
      if (
        confirm(
          intl.formatMessage({
            defaultMessage: "Are you sure you want to delete this message?",
            id: "8+j66D",
          })
        )
      ) {
        deleteMessage([id, accessToken]).then(() => {
          setMessageToEdit(null);
          toast({
            message: intl.formatMessage({
              defaultMessage: "You have successfully deleted the message.",
              id: "RmdTfS",
            }),
          });
        });
      }
    },
    [accessToken, deleteMessage, intl, setMessageToEdit, toast]
  );
  const isDesktop = useScreenMd();
  const isCurrentUserMessage =
    message?.created_by?.id === chatAuthUser?.user.id;
  const isMessageHovered =
    !isNil(messageHovered) && messageHovered === message.id;
  const isMessageSelected = messageToEdit?.id === message.id;

  const openUser = () => {
    push(`/user/${message.created_by?.name}`);
    setIsChatOpen(false);
  };

  return (
    <div
      onClick={partial(setMessageHovered, message?.id ?? null)}
      onMouseOver={partial(setMessageHovered, message?.id ?? null)}
      onMouseLeave={partial(setMessageHovered, null)}
      className={classNames(
        "flex items-center relative mg-xs",
        isCurrentUserMessage ? "justify-start" : "justify-end"
      )}
    >
      <div
        className={classNames(
          "flex flex-col",
          isCurrentUserMessage ? "justify-start" : "justify-end items-end"
        )}
      >
        <div
          className={classNames(
            "flex items-center",
            !isCurrentUserMessage && "justify-end"
          )}
        >
          {isCurrentUserMessage && message?.created_by && (
            <ChatUsersImage user={message.created_by} />
          )}

          <div
            className={classNames(
              "pd-sm pd-l-lg pd-r-lg borderR-1 mg-sm mg-r-md word-break-all",
              messageTheme === MessageTheme.Dark
                ? "bg-light text-white"
                : "bg-gold text-black",
              isCurrentUserMessage
                ? "w-max-90 text-start"
                : "w-max-100 text-end justify-end"
            )}
            dangerouslySetInnerHTML={{
              __html: urlify(convertEmojisToIcons(message?.text ?? "")),
            }}
          />
          {!isCurrentUserMessage && message?.created_by && (
            <ChatUsersImage user={message.created_by} />
          )}
        </div>
        {(isMessageHovered || isMessageSelected) && (
          <span
            className={classNames(
              "text-muted pointer",
              isCurrentUserMessage ? "text-start" : "text-end"
            )}
            onClick={openUser}
          >
            {isCurrentUserMessage && message.created_by?.name}
            {isCurrentUserMessage && " - "}
            {moment(message.created_at).fromNow()}
            {!isCurrentUserMessage && " - "}
            {!isCurrentUserMessage && message.created_by?.name}
          </span>
        )}
      </div>
      {isCurrentUserMessage && !isSending && (
        <div className="h-50px flex items-start justify-center w-min-20">
          {(isMessageHovered || isMessageSelected) && (
            <div
              className={classNames(
                "flex gap-xs",
                isDesktop ? "flex-row" : "flex-col"
              )}
            >
              <span
                className="bg-danger pointer h-30px w-30px flex items-center justify-center borderR-1 mg-r-md"
                onClick={partial(handleDeleteMessage, message?.id ?? null)}
              >
                {isMessageDeleting ? (
                  <LoadingIcon size="sm" />
                ) : (
                  <Icon icon={["far", "trash"]} className="primary" size="sm" />
                )}
              </span>
              <span
                className="bg-gold pointer h-30px w-30px flex items-center justify-center borderR-1 mg-r-md"
                onClick={
                  message?.id && message?.text
                    ? partial(
                        setMessageToEdit,
                        isMessageSelected
                          ? null
                          : {
                              id: message.id,
                              text: message.text,
                            }
                      )
                    : noop
                }
              >
                <Icon
                  icon={isMessageSelected ? ["fal", "times"] : ["fal", "pen"]}
                  color="black"
                  size="sm"
                />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
